import React from 'react';

import { PinFilledIcon, ArrowInCircleRightIcon } from '@vlabs/icons';
import { permissions } from '@vlabs/shared/config';
import { routes } from '@vlabs/shared/navigation/routes';
import type { FaceSampleProps } from '@vlabs/uikit';
import {
  FaceSample, Tooltip, Control, CircleAnimatedButton,
} from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import { viewerCan } from '@vlabs/pages/auth/selectors';

import st from './BaseFaceCard.module.sass';
import type { FaceId } from '@vlabs/api/luna-api/model';

export type TFaceCardSize = 'large' | 'small';

export interface BaseFaceCardProps extends
  Pick<FaceSampleProps, 'src'>,
  WithDataTestid {
  size: TFaceCardSize;
  faceId?: FaceId;
  shouldShowPin?: boolean;
  can: (permission: string[]) => boolean;
}

export function BaseFaceCardComponent({
  size,
  src,
  can,
  faceId = undefined,
  shouldShowPin = false,
}: BaseFaceCardProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();

  const canFaceView = can([permissions.face.view]);

  const onViewFace = () => {
    history.push(generatePath(routes.facePage, { faceId }));
  };

  if (!faceId) return null;

  return (
    <React.Fragment>
      {!!shouldShowPin && (
        <div
          className={st.MainFace}
          data-testid="BaseFaceCard.MainFace"
        >
          <Tooltip
            position="bottom"
            tooltipContent={t('monokit:face card.face.main face')}
          >
            <Control.IconButton
              className={st.MainFaceIcon}
              icon={<PinFilledIcon />}
              size="small"
              variant="dimmed"
            />
          </Tooltip>
        </div>
      )}

      <FaceSample
        buttonOnTop={canFaceView
          ? (
            <CircleAnimatedButton
              data-testid="faceCard.goToButton"
              icon={<ArrowInCircleRightIcon />}
              onClick={onViewFace}
              size="small"
            >
              {t('monokit:face card.buttons.face details')}
            </CircleAnimatedButton>
          ) : undefined}
        className={cn(
          st.BaseFaceCardImage,
          st[`BaseFaceCardImage__${size}`],
        )}
        data-testid="BaseFaceCard.Sample"
        size={size}
        src={src}
      />
    </React.Fragment>
  );
}

export const BaseFaceCard = connect((state) => ({
  can: viewerCan(state),
}))(BaseFaceCardComponent);
